import type {
  LayerSpecification,
  SourceSpecification,
} from "maplibre-gl";

export type LayerGroup =
  | "prices"
  | "people"
  | "risks"
  | "climate"
  | "environment"
  | "transit";

export const GROUP_LABELS: Record<LayerGroup, string> = {
  prices: "Prices",
  people: "People",
  risks: "Risks",
  climate: "Climate",
  environment: "Environment",
  transit: "Transit",
};

/** A legend entry: one colour swatch and what it means. */
export interface LegendStop {
  color: string;
  label: string;
}

/** A named swatch drawn as the thing itself — a transit line's index badge. */
export interface LegendChip {
  label: string;
  color: string;
  textColor: string;
  /** Heading the chip is filed under. Chips sharing one are shown together. */
  group?: string;
}

export interface Legend {
  /** Unit shown once above the stops, e.g. "€/m²" or "dB (Lden)". */
  unit?: string;
  stops: LegendStop[];
  /**
   * Shown instead of a swatch-and-label list where the label belongs *in* the
   * swatch — a colour per line only means something next to the line's index.
   */
  chips?: LegendChip[];
  /** Optional caption, e.g. a WHO threshold or a data caveat. */
  note?: string;
}

/**
 * What the panes are currently set to, for layers whose legend follows a
 * control rather than sitting still.
 *
 * Deliberately plain strings rather than each store's own union type: this
 * module is imported by every layer definition, and importing the stores back
 * would close a cycle. Layers narrow the fields they care about.
 */
export interface LegendContext {
  transitView: string;
  demographicsMetric: string;
  demographicsPeriod: number;
  riskMetric: string;
  riskRecentOnly: boolean;
  climateMetric: string;
  climateScenario: string;
  climateHorizon: string;
  heatWhen: string;
}

/**
 * A togglable map layer.
 *
 * Every definition's sources and MapLibre layers are added to the style **once**,
 * when the map loads. Toggling only flips `visibility` on the already-loaded
 * layers, which is a paint-level change — no network request, no re-parse, no
 * style reflow. This is what keeps the toggles instant rather than janky.
 */
export interface LayerDef {
  /** Stable key, used by the store and the UI. */
  id: string;
  label: string;
  group: LayerGroup;
  /** One line explaining what the layer shows and where the data comes from. */
  description: string;
  /** Attribution line for the data source. */
  attribution: string;
  defaultVisible: boolean;
  sources: Record<string, SourceSpecification>;
  /** MapLibre layers, drawn in array order. */
  layers: LayerSpecification[];
  legend?: Legend;
  /**
   * A legend that depends on what the layer is currently shading.
   *
   * Several layers draw one of many measures — the population choropleth, the
   * climate grid, the hazard counts — and their legend has to follow the
   * control that picks it. Returning it from the definition keeps the promise
   * that adding a layer means appending to the registry: the alternative was a
   * growing `if` chain in `LayerPanel`, one branch per layer.
   *
   * `legend` above is still the static fallback, and what the layer shows on
   * first paint.
   */
  legendFor?: (ctx: LegendContext) => Legend;
  /**
   * This layer draws one year out of a series it carries in full.
   *
   * The year slider is offered only while such a layer is on screen. Most
   * layers here have no time axis at all — a climate horizon, a clay class, a
   * transit network — and a slider hanging over the map with nothing to scrub
   * reads as a control that has stopped working.
   */
  usesYear?: boolean;
  /**
   * Hold this layer's tiles when the user switches it off.
   *
   * MapLibre drops every tile of a source whose layers are all hidden — not
   * into its cache, outright — so switching a layer back on refetches and
   * re-parses from scratch. For air-noise that is 1.7 MB and ~214,000 polygons
   * in one region-wide view, which is why the second draw costs exactly as
   * much as the first.
   *
   * A layer marked here is switched off by painting its `<type>-opacity` to
   * zero instead of hiding it — see `applyVisibility` in `MapView.tsx`, which
   * remembers the original value in `warmLayers`. A fully transparent layer
   * costs nothing per frame, while the source stays "used" and keeps its tiles
   * parsed. The warmth only lasts until the camera moves (see
   * `coolWarmLayers`), because a warm layer goes on fetching tiles for
   * wherever the map travels — worth it for toggling something off and on to
   * compare, not for carrying the heaviest archive in the app around the
   * region invisibly.
   *
   * Because the original opacity is read off the spec object rather than the
   * live map, a warmable layer must declare a literal `<type>-opacity` in its
   * `paint` — an expression or an omitted value has nothing to restore.
   */
  keepWarm?: boolean;
  /**
   * Insert these layers below the style layer with this id. Defaults to the
   * first symbol layer, so data sits under labels and roads stay readable.
   */
  beforeId?: string;
}
